import React, { useState, useEffect, useRef } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";
import { toast } from "react-toastify";
import Robot from "../../images/robothelp.png"
import "./FloatingChat.css";
import { useTranslation } from "react-i18next";
import SendIcon from "../../images/Send.svg";

const TextRenderer = ({ text, className, children }) => {
    const cleanedText = text
        ?.split(/(?=\b[a-d]\))/i)
        .map((part) => part.trim())
        .join('\n\n');

    return (
        <div className={className}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanedText}</ReactMarkdown>
            {children}
        </div>
    );
};

const FloatingChat = ({ textOfVideo, courseLanguage = "uz" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioText, setAudioText] = useState([]);
    const [textToVoice, setTextToVoice] = useState("");
    const [playState, setPlayState] = useState({});
    const [currentAudio, setCurrentAudio] = useState(null);
    const [audioFiles, setAudioFiles] = useState([]);
    const [audioIndex, setAudioIndex] = useState(1);
    const [active, setActive] = useState(false);
    const [isConnectionReady, setIsConnectionReady] = useState(false);
    const { t } = useTranslation()

    const chatBodyRef = useRef(null);
    const inputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const transcriptRef = useRef(audioText?.transcript);

    const socketUrl = `wss://talimbot.soof.uz/chatbot/help?language=${courseLanguage}`;
    const { sendMessage, lastMessage, readyState } = useWebSocket(isOpen ? socketUrl : null);

    const isConnectionOpen = readyState === ReadyState.OPEN;

    // Template for AI
    let templateOfAI = "";
    if (courseLanguage === "ru") {
        templateOfAI = `
        Вы — искусственный интеллект по имени Diyora.
        Ваша задача — отвечать на вопросы студента только на русском языке.
        Не отвечайте на других языках, основываясь на приведенной выше информации,
        если студент не понимает тему.
        Вам категорически запрещено предоставлять информацию, выходящую за рамки указанной выше.
        `;
    } else if (courseLanguage === "en") {
        templateOfAI = `
        You are an artificial intelligence named Diyora. 
        Your task is to answer the student's questions only in English, 
        do not answer any other language, based on the information above if 
        the student does not understand the topic. You are absolutely not allowed to provide information outside of 
        the information above.
        `;
    } else if (courseLanguage === "uz") {
        templateOfAI = `Siz Diyora ismli sun'iy intellektsiz.
        Sizning vazifangiz — faqat o'zbek tilida talabalar savollariga javob berish, boshqa hech qaysi tilda javob bermaslik.
        Agar talaba mavzuni tushunmasa, faqat yuqorida berilgan ma'lumotlarga asoslanib javob bering.
        Sizga yuqoridagi ma'lumotlardan tashqaridagi ma'lumotlarni taqdim etish mutlaqo taqiqlanadi.`;
    }

    // Initialize connection and send initial prompt
    useEffect(() => {
        if (isConnectionOpen && textOfVideo && !isConnectionReady) {
            sendMessage(JSON.stringify(`Information: ${textOfVideo}; Task: ${templateOfAI}`));
            setIsConnectionReady(true);
        }
    }, [isConnectionOpen, textOfVideo, isConnectionReady]);

    // Handle incoming messages
    useEffect(() => {
        if (active && lastMessage !== null) {
            const dateTime = Date.now();
            const botMessage = {
                sender: "bot",
                text: lastMessage.data,
                time: new Date(dateTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                }),
                dateTime,
            };

            setMessages((prev) => {
                const messageExists = prev.some((msg) => msg.text === botMessage.text);
                if (messageExists) return prev;
                return [...prev, botMessage];
            });

            setIsTyping(false);
        }
    }, [lastMessage, active]);

    // Auto scroll to bottom
    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, [messages]);

    // Set text for voice when bot sends message
    useEffect(() => {
        if (messages.length > 0) {
            const lastMsg = messages[messages.length - 1];
            if (lastMsg.sender === "bot" && lastMsg.text) {
                playText(lastMsg.text);
            }
        }
    }, [messages]);


    // Update transcript ref
    useEffect(() => {
        transcriptRef.current = audioText?.transcript;
    }, [audioText.transcript]);

    const handleSend = () => {
        if (input.trim()) {
            const dateTime = Date.now();
            const newMessage = {
                sender: 'user',
                text: input,
                time: new Date(dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                dateTime,
            };

            setActive(true);
            setMessages([...messages, newMessage]);
            sendMessage(`${input}`);
            setInput('');
            setIsTyping(true);
            inputRef.current?.focus();
        }
    };

    // Voice recording
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });
                setAudioBlob(audioBlob);
                sendRecord(audioBlob);
            };

            mediaRecorder.start();
            setIsRecording(true);
        } catch (error) {
            console.error("Ovoz yozib olishda xatolik:", error);
            toast.error("Mikrofonga ruxsat berilmadi!");
        }
    };

    const sendRecord = (blob) => {
        const formData = new FormData();
        formData.append("file", blob);

        axios.post("https://stt.soof.uz/stt", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
                "accept-language": courseLanguage,
            },
        })
            .then((response) => {
                const transcript = response?.data?.transcript;
                setAudioText(response?.data);

                if (transcript && transcript.trim()) {
                    const dateTime = Date.now();
                    const newMessage = {
                        sender: 'user',
                        text: transcript,
                        time: new Date(dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        dateTime,
                    };

                    setActive(true);
                    setMessages((prev) => [...prev, newMessage]);
                    sendMessage(transcript);
                    setIsTyping(true);
                }
            })
            .catch((err) => {
                console.log(err);
                toast.error("Ovozni tanib olishda xatolik!");
            });
    };

    const handleStopAndSend = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    // Text to speech
    const playText = (text) => {
        if (!text || !text.trim()) return;

        axios.post("https://tts.soof.uz/tts", {
            text,
        }, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "accept-language": courseLanguage,
            },
            responseType: "arraybuffer",
        }).then((response) => {
            const audioBlob = new Blob([response.data], { type: "audio/wav" });
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);

            // text yoki unique id bo‘yicha saqlaymiz
            setAudioFiles((prev) => [...prev, { text, url: audioUrl }]);
        }).catch((err) => {
            console.error("Audio load error:", err);
        });
    };


    const playSavedAudio = (text) => {
        const audioFile = audioFiles.find(file => file.text === text);
        if (!audioFile) return;

        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
        }

        const audio = new Audio(audioFile.url);
        setCurrentAudio(audio);
        audio.play()
            .then(() => setPlayState({ [text]: { isPlayVoice: true } }))
            .catch(err => console.error("Audio playback error:", err));

        audio.onended = () => {
            setCurrentAudio(null);
            setPlayState({ [text]: { isPlayVoice: false } });
        };
    };


    useEffect(() => {
        if (typeof textToVoice === "string" && textToVoice.trim() !== "") {
            playText(0);
        }
    }, [textToVoice]);

    const formatDate = () => {
        const date = new Date();
        const day = date.getDate();
        const months = [
            "yanvar", "fevral", "mart", "aprel", "may", "iyun",
            "iyul", "avgust", "sentyabr", "oktyabr", "noyabr", "dekabr",
        ];
        return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };

    const getPlaceholder = () => {
        if (courseLanguage === "ru") return "Ваше сообщение";
        if (courseLanguage === "en") return "Your message";
        return "Xabar yozing";
    };

    const chatTitle = () => {
        if (courseLanguage === "ru") return "Здравствуйте!";
        if (courseLanguage === "en") return "Welcome!";
        return "Assalomu alaykum!";
    };
    const chatDesc = () => {
        if (courseLanguage === "ru") return "Как я могу помочь?";
        if (courseLanguage === "en") return "How can I help you?";
        return "Qanday yordam bera olishim mumkin?";
    };

    const chatName = () => {
        if (courseLanguage === "ru") return "Обращение к преподавателю";
        if (courseLanguage === "en") return "Contact the instructor";
        return "O'qituvchiga murojat";
    };

    const getVoiceButtonText = () => {
        if (courseLanguage === "ru") return isRecording ? "Остановить" : "Голос";
        if (courseLanguage === "en") return isRecording ? "Stop" : "Voice";
        return isRecording ? "To'xtatish" : "Ovoz";
    };

    const getSendButtonText = () => {
        if (courseLanguage === "ru") return "Отправить";
        if (courseLanguage === "en") return "Send";
        return "Yuborish";
    };

    // Draggable functionality
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const chatWindowRef = useRef(null);

    const handleMouseDown = (e) => {
        if (e.target.closest('.floating-chat-header')) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    return (
        <div className="floating-chat-container">
            {isOpen && (
                <div
                    ref={chatWindowRef}
                    className="floating-chat-window"
                    style={{
                        transform: `translate(${position.x}px, ${position.y}px)`,
                        cursor: isDragging ? 'grabbing' : 'default'
                    }}
                    onMouseDown={handleMouseDown}
                >
                    <div className="floating-chat-header" style={{ cursor: 'grab' }}>
                        <div className="chat-header-info">
                            <h3 className="chat-title">{chatName()}</h3>
                            <p className="chat-status">
                                {isConnectionOpen ? "Online" : "Connecting..."}
                            </p>
                        </div>
                        <button
                            className="close-chat-btn"
                            onClick={() => setIsOpen(false)}
                        >
                            ✕
                        </button>
                    </div>

                    <div className="floating-chat-body" ref={chatBodyRef}>
                        <div className="date-badge">
                            <p className="date-text">{formatDate()}</p>
                        </div>

                        {messages.length === 0 && (
                            <div className="welcome-message">
                                <div className="welcome-icon">💬</div>
                                <p className="welcome-title">{chatTitle()}</p>
                                <p className="welcome-subtitle">{chatDesc()}</p>
                            </div>
                        )}

                        {messages.map((msg, index) => (
                            <div key={index} className={`chat-message ${msg.sender === "user" ? "user-message" : "bot-message"}`}>
                                {msg.sender === "user" ? (
                                    <TextRenderer text={msg.text} className="message-content user-content" />
                                ) : (
                                    <div className="bot-message-wrapper">
                                        <TextRenderer text={msg.text} className="message-content bot-content" />
                                        {audioFiles.some(a => a.text === msg.text) && (
                                            <div className="voice-controls">
                                                {!playState[msg.text]?.isPlayVoice ? (
                                                    <button
                                                        className="voice-play-btn"
                                                        onClick={() => playSavedAudio(msg.text)}
                                                    >
                                                        🔊
                                                    </button>
                                                ) : (
                                                    <div className="voice-playing">▶️</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <p className={`message-time ${msg.sender === "user" ? "user-time" : "bot-time"}`}>
                                    {msg.time}
                                </p>
                            </div>
                        ))}


                        {isTyping && (
                            <div className="chat-message bot-message">
                                <div className="typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="floating-chat-input">
                        <button
                            onClick={isRecording ? handleStopAndSend : startRecording}
                            className={`voice-record-btn ${isRecording ? 'recording' : ''}`}
                        >
                            {isRecording ? '⏹️' : '🎤'}
                        </button>
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={getPlaceholder()}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSend()}
                            disabled={!isConnectionOpen}
                            className="chat-text-input"
                        />
                        <button
                            className="send-message-btn"
                            onClick={handleSend}
                            disabled={isTyping || !isConnectionOpen}
                        >
                            <img src={SendIcon} alt="Send" />
                        </button>
                    </div>
                </div>
            )}

            <button
                className={`floating-chat-toggle ${isOpen ? 'hidden' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <img src={Robot} alt="" className="robot-icon" />
            </button>
        </div>
    );
};

export default FloatingChat;